export type EmailNotificationsSource =
  "user" | "autoDisabledOnPushGrant" | "unsubscribeLink";

/**
 * The raw `users/{uid}/profile/emailNotifications` node. `enabled: null`
 * means the user never chose — the effective preference then falls back on
 * whether they have mobile push (see resolver below).
 */
export interface EmailNotificationsPreference {
  enabled: boolean | null;
  source: EmailNotificationsSource | null;
  updatedAt: number | null;
  autoDisableApplied: boolean;
}

const EMAIL_NOTIFICATIONS_SOURCES: readonly EmailNotificationsSource[] = [
  "user",
  "autoDisabledOnPushGrant",
  "unsubscribeLink",
];

export const parseEmailNotificationsPreference = (
  value: unknown,
): EmailNotificationsPreference => {
  const record =
    value && typeof value === "object"
      ? (value as Record<string, unknown>)
      : {};
  return {
    enabled: typeof record.enabled === "boolean" ? record.enabled : null,
    source: EMAIL_NOTIFICATIONS_SOURCES.includes(
      record.source as EmailNotificationsSource,
    )
      ? (record.source as EmailNotificationsSource)
      : null,
    updatedAt:
      typeof record.updatedAt === "number" && Number.isFinite(record.updatedAt)
        ? record.updatedAt
        : null,
    autoDisableApplied: record.autoDisableApplied === true,
  };
};

/**
 * Mirrors the backend's `resolveEmailPreference` exactly, so the Settings
 * toggle always shows what the sender would actually do: an explicit choice
 * wins; otherwise default ON — except for users who already have mobile push
 * (web tokens are tagged in `notificationTokenPlatforms` and never count).
 */
export const resolveEmailNotificationsEnabled = (
  preference: EmailNotificationsPreference,
  hasMobileTokens: boolean,
): boolean => {
  if (preference.enabled !== null) {
    return preference.enabled;
  }
  return !hasMobileTokens;
};

/** At least one registered token that is not tagged "web". */
export const hasMobileNotificationTokens = (
  tokens: unknown,
  platforms: unknown,
): boolean => {
  const tokensRecord =
    tokens && typeof tokens === "object"
      ? (tokens as Record<string, unknown>)
      : {};
  const platformsRecord =
    platforms && typeof platforms === "object"
      ? (platforms as Record<string, unknown>)
      : {};
  return Object.keys(tokensRecord).some(
    (token) => platformsRecord[token] !== "web",
  );
};
